/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */

import { RequestOptions } from "~/types/requestOptions";
import { authenticationService } from "./authentication-service";
import { AggregatedMetrics, Metrics } from "~/types/metrics";

const baseURL = "/api/v1/orch/";

export type MetricsAggQuery = {
  metrics: string[];
  interval: {
    startTime: number;
    endTime: number;
  };
  resolution: number;
  timezone?: number;
  queryKeys?: {
    key: string;
    value: number | string;
  }[];
  labels?: string[];
};

export type MetricsQuery = MetricsAggQuery & {
  ids: string[];
  queryKeys?: {
    key: string;
    value: number | string;
  }[];
  /** Dependency for loading */
  _showLoading?: boolean;
  _aggregate?: boolean;
  _tz?: string;
};

class APIService<T> {
  readonly url: string = baseURL;
  /** When set, used instead of `authenticationService` (fixed credential). Otherwise the current session token is read on each request. */
  private readonly tokenOverride?: string;

  constructor(objectType: string, token?: string) {
    this.url = baseURL + objectType;
    if (token) {
      this.tokenOverride = token;
    }

    // function binding
    this.getAll = this.getAll.bind(this);
    this.create = this.create.bind(this);
    this.update = this.update.bind(this);
    this.delete = this.delete.bind(this);
    this.getMetrics = this.getMetrics.bind(this);
    this.getAggregateMetrics = this.getAggregateMetrics.bind(this);
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.upload = this.upload.bind(this);
  }

  private bearerToken(): string {
    return (this.tokenOverride ?? authenticationService.currentUserValue?.token) as string;
  }

  private jsonAuthHeaders(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.bearerToken()}`,
    };
  }

  /** Parses API JSON body and unwraps `{ data }` when present. */
  private async jsonFromResponse<R>(response: Response): Promise<R> {
    const text = await response.text();
    const result = text && JSON.parse(text);
    return (result?.data ?? result) as R;
  }

  async getAll(queryParams: Record<string, any> = {}) {
    const options: RequestOptions = {
      method: "GET",
      headers: this.jsonAuthHeaders(),
    };

    let url = this.url;

    const query = Object.entries(queryParams)
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    if (query) {
      url = `${url}?${query}`;
    }
    try {
      const response = await fetch(url, options);
      return await this.jsonFromResponse<T[]>(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async create(data: T) {
    const options: RequestOptions = {
      method: "POST",
      headers: this.jsonAuthHeaders(),
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(this.url, options);
      return await this.jsonFromResponse<T>(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async update(data: Partial<T>, id?: string) {
    const options: RequestOptions = {
      method: "PUT",
      headers: this.jsonAuthHeaders(),
      body: JSON.stringify(data),
    };
    try {
      const targetId = id ?? (data as { id?: string }).id;
      const response = await fetch(`${this.url}/${targetId}`, options);
      return await this.jsonFromResponse<T>(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async delete(id: string) {
    const options: RequestOptions = {
      method: "DELETE",
      headers: this.jsonAuthHeaders(),
    };
    try {
      const response = await fetch(`${this.url}/${id}`, options);
      return response;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async getAggregateMetrics(query: MetricsAggQuery) {
    const options: RequestOptions = {
      method: "POST",
      headers: this.jsonAuthHeaders(),
      body: JSON.stringify(query),
    };
    try {
      const response = await fetch(`${this.url}/metrics/aggregate`, options);
      return await this.jsonFromResponse<AggregatedMetrics[]>(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async getMetrics(query: MetricsQuery) {
    const options: RequestOptions = {
      method: "POST",
      headers: this.jsonAuthHeaders(),
      body: JSON.stringify(query),
    };
    try {
      const response = await fetch(`${this.url}/metrics`, options);
      return await this.jsonFromResponse<Metrics[]>(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async get<U = T>(path: string) {
    const options: RequestOptions = {
      method: "GET",
      headers: this.jsonAuthHeaders(),
    };
    try {
      const response = await fetch(`${this.url}/${path}`, options);
      return await this.jsonFromResponse<U>(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async post<U = T>(path: string, data?: U) {
    const options: RequestOptions = {
      method: "POST",
      headers: this.jsonAuthHeaders(),
    };
    if (data && !(data instanceof FormData)) {
      options.body = JSON.stringify(data);
    }
    if (data instanceof FormData) {
      delete options.headers['Content-Type'];
      options.body = data;
    }
    try {
      const response = await fetch(`${this.url}/${path}`, options);
      if (data) {
        return await this.jsonFromResponse<T>(response);
      }
      return response;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async upload(path: string, file: File, metaData?: any) {
    const formData = new FormData();
    formData.append("file", file);
    if (metaData) {
      formData.append("metaData", JSON.stringify(metaData));
    }
    const options: RequestOptions = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.bearerToken()}`,
      },
      body: formData,
    };
    try {
      const response = await fetch(`${this.url}/${path}`, options);
      return await this.jsonFromResponse(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async put<U = T[]>(path: string, payload: U) {
    const options: RequestOptions = {
      method: "PUT",
      headers: this.jsonAuthHeaders(),
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(`${this.url}/${path}`, options);
      return await this.jsonFromResponse<U>(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  /**
   * This does return the raw response from the API. Do only use this if you need the raw response.
   * Example where you would use this is when you need to get the pagination data from the API.
   * @example
   *   "data": [
   *     {
   *       "id": "1",
   *       "name": "John Doe"
   *     }
   *   ],
   *   "paginationData": {
   *     "totalPages": 10,
   */
  async postQuery<U = T[]>(path: string, payload: U) {
    const options: RequestOptions = {
      method: "POST",
      headers: this.jsonAuthHeaders(),
    };
    if (payload) {
      options.body = JSON.stringify(payload);
    }
    try {
      const response = await fetch(`${this.url}/${path}`, options);
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
}

export default APIService;
